import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from '@crew/modules/i18n'
import { useForm } from 'react-hook-form'
import {
  useInsertChatMessageMutation,
  useUpdateChatMessageMutation,
} from '@crew/apis/chat/chatApis'
import { UploadFile } from 'models/domain/uploadFile'
import { EntityType, MessageType } from '@crew/enums/domain'
import { sanitizeHtmlAllowNothing } from 'utils/html'
import { useAiAssistantService, useChatDraftService } from '@crew/states'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { ChatMessage } from '@crew/models/domain'
import { ValidateRules } from '@crew/utils/form'
import { MAXIMUM_AI_ASSISTANT_PROJECTS } from '@crew/configs/constants'
import { RelatedEntity } from '@crew/models/common'

export type FormValues = {
  chatMessage: string
  ragTargetProjectIds: string[] | null
}

export const useCrewChatMessageEditor = (
  threadRootMessageId: string | undefined,
  chatRoomId: string | null,
  message: ChatMessage | undefined,
  isEditMode: boolean
) => {
  const [insertChatMessageMutation] = useInsertChatMessageMutation()
  const [updateChatMessageMutation] = useUpdateChatMessageMutation()

  const dispatch = useAppDispatch()
  const { saveChatDraft, deleteChatDraft } = useChatDraftService(dispatch)
  const aiAssistantService = useAiAssistantService(dispatch)

  // AIアシスタントチャンネルの表示対象エンティティ
  const aiAssistantChannelDisplayTargetEntity = useAppSelector(
    (state) => state.message.chat.aiAssistantChannel.displayTargetEntity
  )

  /**
   * 下書きの取得先ID
   * 下書きは新規投稿時のみ保存されるため、トピックIDまたはチャットルームIDを設定する
   *
   *  threadRootMessageIdが設定されている場合：スレッド形式と判断し、threadRootMessageIdを取得先IDとする
   *  threadRootMessageIdが設定されていない場合：タイムライン形式またはスレッドリスト形式と判断し、chatRoomIdを取得先IDとする
   */
  const draftTargetId = (threadRootMessageId ?? chatRoomId) as string // 新規投稿時、トピックIDまたはチャットルームIDどちらかは必ず設定される想定のため、as stringでキャスト

  // 現在の下書きの取得先ID
  const [currentDraftTargetId, setCurrentDraftTargetId] =
    useState(draftTargetId)

  const defaultValues: FormValues = {
    chatMessage: message?.text ?? '',
    ragTargetProjectIds: [],
  }

  const { control, handleSubmit, reset, formState, getValues, setError } =
    useForm<FormValues>({
      defaultValues: defaultValues,
      criteriaMode: 'all',
    })

  const { t } = useTranslation()

  // 添付ファイル（アップロードファイル）情報格納用の配列
  const [uploadedFileList, setUploadedFileList] = useState<UploadFile[]>([])

  // ファイルアップロード中かどうか
  const isFileUploading = useMemo(() => {
    return uploadedFileList.some((file) => file.progress)
  }, [uploadedFileList])

  const messageRequiredValidateRule = useCallback(() => {
    const message = getValues('chatMessage')
    // get plain text from html
    const plainText = sanitizeHtmlAllowNothing(message)

    // invalid file uploading
    if (isFileUploading) {
      return t('message.general.invalidFileUploading')
    }

    if (!plainText && uploadedFileList.length === 0)
      return t('message.general.required')

    return true
  }, [getValues, isFileUploading, uploadedFileList.length, t])

  // プロジェクトIDの最大入力件数のチェック
  const validProjectIdsMaxLength = useCallback(() => {
    const projectIds = getValues('ragTargetProjectIds')
    if (projectIds && projectIds.length > MAXIMUM_AI_ASSISTANT_PROJECTS) {
      return false
    }
    return true
  }, [getValues])

  // バリデーションルール
  const validateRules: ValidateRules<FormValues> = useMemo(
    () => ({
      chatMessage: {
        validate: messageRequiredValidateRule,
      },
      ragTargetProjectIds: {
        required: t('message.general.required'),
        validate: {
          validProjectIdsMaxLength: () =>
            validProjectIdsMaxLength() ||
            t('message.chat.invalidMaxProjectIds', {
              max: MAXIMUM_AI_ASSISTANT_PROJECTS,
            }),
        },
      },
    }),
    [messageRequiredValidateRule, t, validProjectIdsMaxLength]
  )

  // update message
  const updateMessage = useCallback(
    async (
      data: FormValues,
      chatMessageId: string,
      version: number,
      mentionUserIds: string[] | undefined,
      relatedEntities: RelatedEntity[] | undefined
    ) => {
      await updateChatMessageMutation({
        chatMessage: {
          chatMessageId,
          text: data.chatMessage,
          version,
        },
        mentionUserIds,
        relatedTargets: relatedEntities,
      }).unwrap()
    },
    [updateChatMessageMutation]
  )

  // insert message
  const insertMessage = useCallback(
    async (
      data: FormValues,
      messageType: MessageType,
      chatRoomId: string | undefined,
      parentChatMessageId: string | undefined,
      uploadedFileList: UploadFile[] | undefined,
      mentionUserIds: string[] | undefined,
      relatedEntities: RelatedEntity[] | undefined
    ) => {
      // RAG対象
      const ragTarget = () => {
        // スレッド表示（parentChatMessageId !== undefined）の場合、RAG対象は設定しないのでundefinedを返す
        if (parentChatMessageId !== undefined) {
          return undefined
        }

        // AIアシスタントチャンネルの表示対象エンティティが設定されている場合、RAG対象はそちらの値を使用する
        if (aiAssistantChannelDisplayTargetEntity) {
          return [
            {
              entityType: aiAssistantChannelDisplayTargetEntity.entityType,
              entityRecordId:
                aiAssistantChannelDisplayTargetEntity.entityRecordId,
            },
          ]
        }

        // AIアシスタントチャンネルの表示対象エンティティが設定されていない場合、フォームに入力された値を使用する
        if (
          !data.ragTargetProjectIds ||
          data.ragTargetProjectIds.length === 0
        ) {
          return undefined
        }
        return data.ragTargetProjectIds.map((projectId) => ({
          entityType: EntityType.Project,
          entityRecordId: projectId,
        }))
      }

      return await insertChatMessageMutation({
        chatMessage: {
          messageType,
          text: data.chatMessage,
          chatRoomId,
          parentChatMessageId,
          files: uploadedFileList,
        },
        mentionUserIds,
        ragTargets: ragTarget(),
        relatedTargets: relatedEntities,
      }).unwrap()
    },
    [aiAssistantChannelDisplayTargetEntity, insertChatMessageMutation]
  )

  // 下書きをreduxに保存する
  const saveDraft = useCallback(() => {
    // 編集時は下書き保存しないため、何もしない
    if (isEditMode) {
      return
    }

    const message = getValues('chatMessage')
    const plainText = sanitizeHtmlAllowNothing(message)

    if (!plainText && uploadedFileList.length === 0) {
      // 「メッセージが空」かつ「添付ファイルがない」場合：下書きをreduxから削除
      deleteChatDraft(currentDraftTargetId)
    } else {
      // メッセージが入力されているか、添付ファイルがある場合：下書きをreduxに保存
      saveChatDraft({
        id: currentDraftTargetId,
        text: message,
        files: uploadedFileList,
      })
    }
  }, [
    currentDraftTargetId,
    deleteChatDraft,
    getValues,
    isEditMode,
    saveChatDraft,
    uploadedFileList,
  ])

  // プロジェクトIDをreduxに保存する
  const saveProjectIds = useCallback(() => {
    // 編集時はプロジェクトIDを編集しないため、何もしない
    if (isEditMode) {
      return
    }
    const projectIds = getValues('ragTargetProjectIds')
    aiAssistantService.setProjectsIds({ projectIds: projectIds ?? undefined })
  }, [aiAssistantService, getValues, isEditMode])

  return {
    control,
    validateRules,
    uploadedFileList,
    setUploadedFileList,
    currentDraftTargetId,
    setCurrentDraftTargetId,
    getValues,
    reset,
    setError,
    handleSubmit,
    updateMessage,
    insertMessage,
    saveDraft,
    deleteChatDraft,
    formState,
    isFileUploading,
    saveProjectIds,
  }
}
